"use strict";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import { ModalState, miniPortfolioModalStateAtom } from "components/AccountDrawer/constants";
import { useAccount } from "hooks/useAccount";
import { useEthersProvider } from "hooks/useEthersProvider";
import { useEthersSigner } from "hooks/useEthersSigner";
import { useModalState } from "hooks/useModalState";
import { useShowSwapNetworkNotification } from "hooks/useShowSwapNetworkNotification";
import { useUpdateAtom } from "jotai/utils";
import { useOneClickSwapSetting } from "pages/Swap/settings/OneClickSwap";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { serializeSwapAddressesToURLParameters } from "state/swap/hooks";
import { useGetGeneratePermitAsTransaction } from "state/walletCapabilities/hooks/useGetGeneratePermitAsTransaction";
import { useIsAtomicBatchingSupportedByChainIdCallback } from "state/walletCapabilities/hooks/useIsAtomicBatchingSupportedByChain";
import { useHasMismatchCallback, useShowMismatchToast } from "state/walletCapabilities/hooks/useMismatchAccount";
import { useSetActiveChainId } from "state/wallets/hooks";
import { UniswapProvider } from "contexts/UniswapContext";
import { AccountType } from "features/accounts/types";
import { getChainInfo } from "features/chains/chainInfo";
import { useEnabledChainsWithConnector } from "features/chains/hooks/useEnabledChains";
import { FeatureFlags } from "features/gating/flags";
import { useFeatureFlag } from "features/gating/hooks";
import { MismatchContextProvider } from "features/smartWallet/mismatch/MismatchContext";
import { useIsSmartContractWallet } from "features/transactions/swap/contexts/hooks/useIsSmartContractWallet";
import { ModalName } from "features/telemetry/constants";
import { currencyIdToAddress, currencyIdToChain } from "utils/uniswapCurrencyId";
import { getTokenDetailsURL } from "utils/linking";
import { useEvent, usePrevious } from "utilities/src/react/hooks";
import noop from "utilities/src/react/noop";
function useWebProvider(chainId) {
  return useEthersProvider({ chainId });
}
function useWagmiAccount() {
  const account = useAccount();
  return useMemo(() => {
    if (!account.address) {
      return {
        account: void 0,
        connector: account.connector
      };
    }
    return {
      account: {
        address: account.address,
        type: AccountType.SignerMnemonic
      },
      connector: account.connector
    };
  }, [account.address, account.connector]);
}
export function WebUniswapProvider({ children }) {
  return <MismatchContextWrapper>
      <WebUniswapProviderInner>{children}</WebUniswapProviderInner>
    </MismatchContextWrapper>;
}
function WebUniswapProviderInner({ children }) {
  const { account, connector } = useWagmiAccount();
  const { chainId: walletChainId } = useAccount();
  const signer = useEthersSigner();
  const showSwapNetworkNotification = useShowSwapNetworkNotification();
  const accountDrawer = useAccountDrawer();
  const navigate = useNavigate();
  const navigateToFiatOnRamp = useCallback(() => navigate(`/buy`, { replace: true }), [navigate]);
  const { closeModal: closeSearchModal } = useModalState(ModalName.Search);
  const navigateToSwapFlow = useCallback(
    ({ inputCurrencyId, outputCurrencyId }) => {
      const queryParams = serializeSwapAddressesToURLParameters({
        inputTokenAddress: inputCurrencyId ? currencyIdToAddress(inputCurrencyId) : void 0,
        outputTokenAddress: outputCurrencyId ? currencyIdToAddress(outputCurrencyId) : void 0,
        chainId: inputCurrencyId ? currencyIdToChain(inputCurrencyId) : void 0,
        outputChainId: outputCurrencyId ? currencyIdToChain(outputCurrencyId) : void 0
      });
      navigate(`/swap${queryParams}`, { replace: true });
      closeSearchModal();
    },
    [navigate, closeSearchModal]
  );
  const navigateToSendFlow = useCallback(
    ({ chainId, currencyAddress }) => {
      const chainUrlParam = getChainInfo(chainId).urlParam;
      navigate(`/send?chain=${chainUrlParam}&inputCurrency=${currencyAddress}`, { replace: true });
      closeSearchModal();
    },
    [navigate, closeSearchModal]
  );
  const setReceiveModalState = useUpdateAtom(miniPortfolioModalStateAtom);
  const navigateToReceive = useCallback(() => setReceiveModalState(ModalState.QR_CODE), [setReceiveModalState]);
  const handleShareToken = useCallback((_) => {
    noop();
  }, []);
  const navigateToTokenDetails = useCallback(
    async (currencyId) => {
      const url = getTokenDetailsURL({
        address: currencyIdToAddress(currencyId),
        chain: currencyIdToChain(currencyId) ?? void 0
      });
      navigate(url);
      closeSearchModal();
    },
    [navigate, closeSearchModal]
  );
  const navigateToPoolDetails = useCallback(
    ({ poolAddress, chainId }) => {
      const chainName = getChainInfo(chainId).urlParam;
      navigate(`/explore/pools/${chainName}/${poolAddress}`);
      closeSearchModal();
    },
    [navigate, closeSearchModal]
  );
  const getIsUniswapXSupported = useEvent((_innerChainId) => false);
  const getGeneratePermitAsTransaction = useGetGeneratePermitAsTransaction();
  const navigateToExternalProfile = useCallback((_) => noop(), []);
  const navigateToNftCollection = useCallback((args) => {
    window.open(
      `https://opensea.io/assets/${getChainInfo(
        args.chainId
      ).backendChain.chain.toLowerCase()}/${args.collectionAddress}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);
  const { openModal } = useModalState(ModalName.DelegationMismatch);
  const handleOpenUniswapXUnsupportedModal = useEvent(() => {
    openModal();
  });
  const isBatchedSwapsFlagEnabled = useFeatureFlag(FeatureFlags.BatchedSwaps);
  const isAtomicBatchingSupportedByChain = useIsAtomicBatchingSupportedByChainIdCallback();
  const { enabled: isOneClickSwapSettingEnabled } = useOneClickSwapSetting();
  const { isSmartContract: isAccountSmartContract } = useIsSmartContractWallet(account?.address, walletChainId);
  const getCanBatchTransactions = useEvent((chainId) => {
    if (connector?.id === "safe" || isAccountSmartContract) {
      return true;
    }
    if (chainId === 97 || chainId === 56) {
      return false;
    }
    return Boolean(
      isBatchedSwapsFlagEnabled && isOneClickSwapSettingEnabled && chainId && isAtomicBatchingSupportedByChain(chainId)
    );
  });
  const setActiveChainId = useSetActiveChainId();
  const onSwapChainsChanged = useEvent(
    ({ chainId, prevChainId }) => {
      setActiveChainId(chainId);
      showSwapNetworkNotification({ chainId, prevChainId });
    }
  );
  useAccountChainIdEffect();
  return <UniswapProvider
    account={account}
    signer={signer}
    connector={connector}
    useProviderHook={useWebProvider}
    onSwapChainsChanged={onSwapChainsChanged}
    navigateToFiatOnRamp={navigateToFiatOnRamp}
    navigateToSwapFlow={navigateToSwapFlow}
    navigateToSendFlow={navigateToSendFlow}
    navigateToReceive={navigateToReceive}
    navigateToTokenDetails={navigateToTokenDetails}
    navigateToPoolDetails={navigateToPoolDetails}
    navigateToExternalProfile={navigateToExternalProfile}
    navigateToNftCollection={navigateToNftCollection}
    handleShareToken={handleShareToken}
    onConnectWallet={accountDrawer.open}
    getGeneratePermitAsTransaction={getGeneratePermitAsTransaction}
    getIsUniswapXSupported={getIsUniswapXSupported}
    handleOnPressUniswapXUnsupported={handleOpenUniswapXUnsupportedModal}
    getCanBatchTransactions={getCanBatchTransactions}
  >
      {children}
    </UniswapProvider>;
}
const MismatchContextWrapper = React.memo(function MismatchContextWrapper2({ children }) {
  const getHasMismatch = useHasMismatchCallback();
  const account = useAccount();
  const onHasAnyMismatch = useShowMismatchToast();
  const { chains, defaultChainId, isTestnetModeEnabled } = useEnabledChainsWithConnector(account.connector);
  return <MismatchContextProvider
    mismatchCallback={getHasMismatch}
    address={account?.address}
    chainId={account?.chainId}
    onHasAnyMismatch={onHasAnyMismatch}
    chains={chains}
    defaultChainId={defaultChainId}
    isTestnetModeEnabled={isTestnetModeEnabled}
  >
      {children}
    </MismatchContextProvider>;
});
MismatchContextWrapper.displayName = "MismatchContextWrapper";
function useAccountChainIdEffect() {
  const currentChainId = useSelector((state) => state.wallets.activeChainId);
  const { chainId } = useAccount();
  const prevChainId = usePrevious(chainId);
  const setActiveChainId = useSetActiveChainId();
  useEffect(() => {
    if (!chainId) return;
    if (!currentChainId) {
      setActiveChainId(chainId);
    } else if (prevChainId !== chainId) {
      setActiveChainId(chainId);
    }
  }, [chainId, currentChainId, prevChainId, setActiveChainId]);
}
